import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  due_date: string | null;
  scrum_active: boolean;
  total_value: number | null;
  estimated_hours: number | null;
}

const STATUS_STYLE: Record<string, string> = {
  ativo: 'bg-emerald-100 text-emerald-700',
  pausado: 'bg-orange-100 text-orange-700',
  concluido: 'bg-sky-100 text-sky-700',
  cancelado: 'bg-red-100 text-red-700',
  planejamento: 'bg-slate-100 text-slate-600',
};

interface Props {
  clientId: string;
  clientName: string;
}

export default function ClientProjectsTab({ clientId, clientName }: Props) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('projects')
      .select('id, name, description, status, start_date, due_date, scrum_active, total_value, estimated_hours')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    setProjects(data || []);
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
  const fmtCurrency = (v: number | null) => v != null ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-slate-500 text-sm">{projects.length} projeto{projects.length !== 1 ? 's' : ''}</p>
        <Link
          to={`/admin/projetos/novo?client_id=${clientId}&client_name=${encodeURIComponent(clientName)}`}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Novo Projeto
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-xl"></i>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <i className="ri-folder-line text-4xl mb-3"></i>
          <p className="text-sm">Nenhum projeto cadastrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-cyan-200 transition-colors">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-slate-800 font-semibold text-sm">{p.name}</h4>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[p.status] || 'bg-slate-100 text-slate-600'}`}>
                    {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                  </span>
                  {p.scrum_active && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">Scrum</span>
                  )}
                </div>
              </div>
              {p.description && <p className="text-slate-500 text-xs mb-3 line-clamp-2">{p.description}</p>}
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                <div><span className="text-slate-400">Início:</span> {fmt(p.start_date)}</div>
                <div><span className="text-slate-400">Previsão:</span> {fmt(p.due_date)}</div>
                {p.total_value != null && <div><span className="text-slate-400">Valor:</span> {fmtCurrency(p.total_value)}</div>}
                {p.estimated_hours != null && <div><span className="text-slate-400">Horas:</span> {p.estimated_hours}h</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
